import { useTranslation } from '@portals-platform/i18n';
import { SectionCard } from '@portals-platform/ui';
import {
  Building2Icon,
  CalendarClockIcon,
  CalendarIcon,
  FileTextIcon,
  GlobeIcon,
  HashIcon,
  MapPinIcon,
  UserIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import type { CertificateDetails } from '../../data-access/index.js';
import { EMPTY_VALUE, formatLongDate, formatMoney, orEmpty } from './certificate-format.js';
import { StorageLocationsTable } from './storage-locations-table.js';

function ReadonlyField({
  icon,
  label,
  value,
  fullWidth = false,
}: {
  icon?: ReactNode;
  label: string;
  value: string;
  fullWidth?: boolean;
}) {
  return (
    <div className={fullWidth ? 'min-w-0 md:col-span-2' : 'min-w-0'}>
      <dt className="mb-1 flex items-center gap-1.5 text-[12px] text-muted-foreground [&>svg]:size-3.5">
        {icon}
        {label}
      </dt>
      <dd className="whitespace-pre-wrap text-[13.5px] font-semibold wrap-anywhere text-foreground">
        {value}
      </dd>
    </div>
  );
}

function FieldGrid({ children }: { children: ReactNode }) {
  return <dl className="grid grid-cols-1 gap-x-10 gap-y-5 md:grid-cols-2">{children}</dl>;
}

export function CertificateDetailsContent({ certificate }: { certificate: CertificateDetails }) {
  const { t } = useTranslation();
  const field = (key: string) => t(`certificates.details.fields.${key}`);

  return (
    <div className="flex flex-col gap-6">
      <SectionCard title={t('certificates.details.sections.certificateDetails')}>
        <FieldGrid>
          <ReadonlyField
            icon={<UserIcon aria-hidden="true" />}
            label={field('customerName')}
            value={orEmpty(certificate.certificateIssuedTo)}
          />
          <ReadonlyField
            icon={<GlobeIcon aria-hidden="true" />}
            label={field('country')}
            value={orEmpty(certificate.country)}
          />
          <ReadonlyField
            icon={<MapPinIcon aria-hidden="true" />}
            label={field('customerAddress')}
            value={orEmpty(certificate.customerAddress)}
          />
          <ReadonlyField
            icon={<HashIcon aria-hidden="true" />}
            label={field('policyNumber')}
            value={orEmpty(certificate.policyNumber)}
          />
          <ReadonlyField
            icon={<CalendarClockIcon aria-hidden="true" />}
            label={field('policyYear')}
            value={certificate.policyYear === null ? EMPTY_VALUE : String(certificate.policyYear)}
          />
          <ReadonlyField
            icon={<FileTextIcon aria-hidden="true" />}
            label={field('templateType')}
            value={orEmpty(certificate.templateName)}
          />
        </FieldGrid>
      </SectionCard>

      <SectionCard title={t('certificates.details.sections.policyCoverage')}>
        <FieldGrid>
          <ReadonlyField
            icon={<Building2Icon aria-hidden="true" />}
            label={field('contractingEntity')}
            value={orEmpty(certificate.contractingEntity)}
          />
          <ReadonlyField
            icon={<CalendarIcon aria-hidden="true" />}
            label={field('policyExpiry')}
            value={formatLongDate(certificate.policyExpirationDate)}
          />
          <ReadonlyField label={field('currency')} value={orEmpty(certificate.currency)} />
          <ReadonlyField
            label={field('transitLimit')}
            value={formatMoney(certificate.transitLimit)}
          />
        </FieldGrid>

        {certificate.storageLocations.length > 0 && (
          <div className="mt-6">
            <h3 className="mb-2 text-[13px] font-semibold text-foreground">
              {field('storageLocations')}
            </h3>
            <StorageLocationsTable storageLocations={certificate.storageLocations} />
          </div>
        )}
      </SectionCard>

      <SectionCard title={t('certificates.details.sections.submission')}>
        <FieldGrid>
          <ReadonlyField label={field('createdBy')} value={orEmpty(certificate.createdBy)} />
          <ReadonlyField label={field('createdOn')} value={formatLongDate(certificate.createdAt)} />
          {certificate.additionalComments.trim() && (
            <ReadonlyField
              fullWidth
              label={field('additionalComments')}
              value={certificate.additionalComments}
            />
          )}
        </FieldGrid>
      </SectionCard>
    </div>
  );
}
